import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Package, Loader2 } from 'lucide-react';
import { Category } from '@/app/types/category';

interface CategoryUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyUpdate: (categoryId: number | null) => void;
  selectedItemCount: number;
  categories: Category[];
  isUpdating?: boolean;
}

export default function CategoryUpdateDialog({
  open,
  onOpenChange,
  onApplyUpdate,
  selectedItemCount,
  categories,
  isUpdating = false,
}: CategoryUpdateDialogProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const handleApply = () => {
    const categoryId = selectedCategoryId === 'null' ? null : parseInt(selectedCategoryId, 10);
    onApplyUpdate(categoryId);
    onOpenChange(false);
    setSelectedCategoryId(''); // Reset selection
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setSelectedCategoryId(''); // Reset when closing
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Category</DialogTitle>
          <DialogDescription>
            Update the category for {selectedItemCount} selected item{selectedItemCount !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <Label className="text-base font-medium">Select Category</Label>
            <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="null">
                  <span className="text-muted-foreground">No Category</span>
                </SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={String(category.id)}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />
              This will update the category for{' '}
              <span className="font-medium text-foreground">
                {selectedItemCount} item{selectedItemCount !== 1 ? 's' : ''}
              </span>.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selectedCategoryId || isUpdating}
          >
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Apply Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
